import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Save, Loader2, LogOut, KeyRound } from "lucide-react";
import { useAuth, authHeaders } from "@/hooks/useAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.76, 0, 0.24, 1];

const errorText = (d) =>
    !d
        ? "Something went wrong."
        : typeof d === "string"
            ? d
            : Array.isArray(d)
                ? d.map((e) => e?.msg || "").filter(Boolean).join(" ")
                : d?.msg || "Something went wrong.";

export default function Account() {
    const { user, token, setSession, logout, loading } = useAuth();
    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        nickname: "",
        email: "",
        stay_informed: true,
        notify_replies: true,
    });
    const [status, setStatus] = useState(null);
    const [saving, setSaving] = useState(false);

    // Password change
    const [pwCurrent, setPwCurrent] = useState("");
    const [pwNew, setPwNew] = useState("");
    const [pwStatus, setPwStatus] = useState(null);
    const [pwBusy, setPwBusy] = useState(false);

    useEffect(() => {
        if (!loading && !token) {
            navigate("/");
        }
    }, [loading, token, navigate]);

    useEffect(() => {
        if (user) {
            setForm({
                name: user.name || "",
                nickname: user.nickname || "",
                email: user.email || "",
                stay_informed:
                    user.stay_informed !== undefined ? !!user.stay_informed : true,
                notify_replies:
                    user.notify_replies !== undefined ? !!user.notify_replies : true,
            });
        }
    }, [user]);

    const update = (field) => (e) => {
        const val =
            field === "stay_informed" || field === "notify_replies"
                ? e.target.checked
                : e.target.value;
        setForm((f) => ({ ...f, [field]: val }));
    };

    const saveProfile = async (e) => {
        e.preventDefault();
        setStatus(null);
        setSaving(true);
        try {
            const res = await axios.put(`${API}/me`, form, {
                headers: authHeaders(),
            });
            // If email changed, backend returns a new token — refresh session.
            setSession(res.data.token || token, res.data.user);
            setStatus({ type: "success", msg: "Profile updated." });
        } catch (err) {
            setStatus({
                type: "error",
                msg: errorText(err.response?.data?.detail),
            });
        } finally {
            setSaving(false);
        }
    };

    const changePassword = async (e) => {
        e.preventDefault();
        setPwStatus(null);
        setPwBusy(true);
        try {
            await axios.post(
                `${API}/auth/change-password`,
                { current_password: pwCurrent, new_password: pwNew },
                { headers: authHeaders() },
            );
            setPwCurrent("");
            setPwNew("");
            setPwStatus({ type: "success", msg: "Password changed." });
        } catch (err) {
            setPwStatus({
                type: "error",
                msg: errorText(err.response?.data?.detail),
            });
        } finally {
            setPwBusy(false);
        }
    };

    if (loading || !user) {
        return (
            <div className="min-h-screen flex items-center justify-center font-mono text-[13px] uppercase tracking-[0.24em] text-[color:var(--slate)]">
                <Loader2 size={16} className="animate-spin mr-3" />
                Loading your account
            </div>
        );
    }

    return (
        <div
            data-testid="account-page"
            className="min-h-screen text-[color:var(--charcoal)]"
        >
            <div className="mx-auto max-w-3xl px-6 md:px-10 py-14 md:py-20">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE }}
                >
                    <Link
                        to="/"
                        data-testid="account-back"
                        className="inline-flex items-center gap-2 font-mono font-semibold text-[12px] uppercase tracking-[0.24em] text-[color:var(--slate)] hover:text-[color:var(--charcoal)] transition-colors mb-8"
                    >
                        <ArrowLeft size={13} strokeWidth={1.6} />
                        Back to the manifesto
                    </Link>

                    <div className="font-mono font-semibold text-[12px] uppercase tracking-[0.28em] text-[color:var(--slate)] mb-3">
                        Your account
                    </div>
                    <h1 className="font-display text-[2.2rem] md:text-[3rem] lg:text-[3.5rem] leading-[0.95] tracking-[-0.02em] mb-2">
                        Hi, {user.nickname || user.name}.
                    </h1>
                    <p className="font-body text-base md:text-lg text-[color:var(--charcoal)]/85 max-w-[62ch]">
                        Update your details, stay in the loop, or change your
                        password below.
                    </p>
                </motion.div>

                {/* Profile form */}
                <form
                    onSubmit={saveProfile}
                    data-testid="profile-form"
                    className="mt-10 md:mt-14 border-t border-[color:var(--charcoal)]/15 pt-8"
                >
                    <h2 className="font-display text-xl md:text-2xl mb-6">
                        Details
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                        <Field
                            label="Full name"
                            testId="profile-name"
                            value={form.name}
                            onChange={update("name")}
                        />
                        <Field
                            label="Nickname (shown publicly)"
                            testId="profile-nickname"
                            value={form.nickname}
                            onChange={update("nickname")}
                        />
                        <Field
                            label="Email"
                            testId="profile-email"
                            type="email"
                            value={form.email}
                            onChange={update("email")}
                        />
                    </div>

                    <label className="mt-6 flex items-start gap-3 cursor-pointer group">
                        <input
                            type="checkbox"
                            checked={form.stay_informed}
                            onChange={update("stay_informed")}
                            data-testid="profile-stay-informed"
                            className="mt-1 h-4 w-4 accent-[color:var(--charcoal)]"
                        />
                        <span>
                            <span className="block font-mono font-semibold text-[12px] uppercase tracking-[0.22em] text-[color:var(--charcoal)]">
                                Stay Informed
                            </span>
                            <span className="block font-body text-sm text-[color:var(--charcoal)]/85 max-w-[52ch] mt-1">
                                Send me a brief note when a new manifesto
                                chapter, article, or podcast episode drops.
                                No newsletters, no marketing — just the
                                signal.
                            </span>
                        </span>
                    </label>

                    <label className="mt-4 flex items-start gap-3 cursor-pointer group">
                        <input
                            type="checkbox"
                            checked={form.notify_replies}
                            onChange={update("notify_replies")}
                            data-testid="profile-notify-replies"
                            className="mt-1 h-4 w-4 accent-[color:var(--charcoal)]"
                        />
                        <span>
                            <span className="block font-mono font-semibold text-[12px] uppercase tracking-[0.22em] text-[color:var(--charcoal)]">
                                Notify me on replies
                            </span>
                            <span className="block font-body text-sm text-[color:var(--charcoal)]/85 max-w-[52ch] mt-1">
                                Email me when someone replies to a comment I
                                left. Turn this off to keep your inbox quiet.
                            </span>
                        </span>
                    </label>

                    {status && (
                        <div
                            data-testid="profile-status"
                            className={`mt-6 p-3 border font-body text-sm ${
                                status.type === "success"
                                    ? "border-[color:var(--charcoal)] bg-[color:var(--charcoal)]/5"
                                    : "border-red-800 text-red-900"
                            }`}
                        >
                            {status.msg}
                        </div>
                    )}

                    <div className="mt-6 flex gap-3 flex-wrap">
                        <button
                            type="submit"
                            disabled={saving}
                            data-testid="profile-save"
                            className="pill-btn disabled:opacity-60"
                            style={{ padding: "0.7rem 1.3rem" }}
                        >
                            {saving ? (
                                <>
                                    <Loader2 size={14} className="animate-spin" />
                                    Saving
                                </>
                            ) : (
                                <>
                                    <Save size={14} strokeWidth={1.6} />
                                    Save details
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                logout();
                                navigate("/");
                            }}
                            data-testid="profile-logout"
                            className="pill-btn ghost"
                            style={{ padding: "0.7rem 1.3rem" }}
                        >
                            <LogOut size={14} strokeWidth={1.6} />
                            Sign out
                        </button>
                    </div>
                </form>

                {/* Password */}
                <form
                    onSubmit={changePassword}
                    data-testid="password-form"
                    className="mt-14 border-t border-[color:var(--charcoal)]/15 pt-8"
                >
                    <h2 className="font-display text-xl md:text-2xl mb-6 flex items-center gap-3">
                        <KeyRound size={20} strokeWidth={1.4} />
                        Change password
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                        <Field
                            label="Current password"
                            testId="password-current"
                            type="password"
                            value={pwCurrent}
                            onChange={(e) => setPwCurrent(e.target.value)}
                            autoComplete="current-password"
                        />
                        <Field
                            label="New password (8+ chars)"
                            testId="password-new"
                            type="password"
                            value={pwNew}
                            onChange={(e) => setPwNew(e.target.value)}
                            autoComplete="new-password"
                            minLength={8}
                        />
                    </div>

                    {pwStatus && (
                        <div
                            data-testid="password-status"
                            className={`mt-6 p-3 border font-body text-sm ${
                                pwStatus.type === "success"
                                    ? "border-[color:var(--charcoal)] bg-[color:var(--charcoal)]/5"
                                    : "border-red-800 text-red-900"
                            }`}
                        >
                            {pwStatus.msg}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={pwBusy || !pwCurrent || pwNew.length < 8}
                        data-testid="password-save"
                        className="mt-6 pill-btn disabled:opacity-60"
                        style={{ padding: "0.7rem 1.3rem" }}
                    >
                        {pwBusy ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />
                                Updating
                            </>
                        ) : (
                            <>
                                <KeyRound size={14} strokeWidth={1.6} />
                                Update password
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}

const Field = ({
    label,
    testId,
    value,
    onChange,
    type = "text",
    autoComplete,
    minLength,
}) => (
    <label className="block">
        <span className="block font-mono font-semibold text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
            {label}
        </span>
        <input
            type={type}
            value={value}
            onChange={onChange}
            data-testid={testId}
            autoComplete={autoComplete}
            minLength={minLength}
            className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-body text-base"
        />
    </label>
);
